import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { api } from '../../api/endpoints';
import type { Area } from '../../api/types';
import { EmptyState } from '../../components/EmptyState';
import { ErrorMessage } from '../../components/ErrorMessage';
import { PageLoader } from '../../components/Loader';
import { MutationError } from '../../components/MutationError';
import { areaStatusLabels, elementTypeLabels, responsibilityLabels, ruleText } from '../../geo/labels';
import { useProject } from '../../projects/context';
import { isRemote } from '../../projects/kind';
import { projectKeys, useProjectApi } from '../../projects/useProjectApi';

// Gebiete des aktiven Projekts: eigene und zugeordnete projektübergreifende Gebiete.
export function AreasPage({ embedded = false }: { embedded?: boolean }) {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const [showInactive, setShowInactive] = useState(false);
  const areas = useQuery({
    queryKey: [...projectKeys.areas(project.id), showInactive ? 'all' : 'active'],
    queryFn: ({ signal }) => papi.projectAreas(showInactive, signal),
  });

  return (
    <div className={embedded ? 'embedded-page' : 'page'}>
      <header className="page-header">
        {!embedded && <h1>Gebiete</h1>}
        <label className="checkbox">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          Deaktivierte anzeigen
        </label>
      </header>
      {isRemote(project) && (
        <p className="alert alert-info" role="note">
          <strong>Freigegebenes Remote-Gebiet:</strong> Dieses Online-Remote-Projekt nimmt nur Datensätze aus seinen aktiven Gebieten
          auf. Ausgeschlossen bleiben Datensätze, die je einem normalen Projekt zugeordnet waren, auch wenn das Projekt beendet ist,
          sowie Adressen im Gebiet eines aktiven normalen Projekts.
        </p>
      )}
      {can('area.create') && <CreateArea />}
      {areas.isPending ? (
        <PageLoader label="Gebiete werden geladen …" />
      ) : areas.isError ? (
        <ErrorMessage error={areas.error} onRetry={() => void areas.refetch()} />
      ) : areas.data.items.length === 0 ? (
        <EmptyState title="Noch keine Gebiete" icon="map">
          Diesem Projekt sind noch keine Gebiete zugeordnet. Legen Sie mit „Neues Gebiet“ das erste an und ordnen Sie Landkreise, Gemeinden oder PLZ zu.
        </EmptyState>
      ) : (
        <ul className="card-grid">
          {areas.data.items.map((a) => (
            <AreaCard key={a.id} area={a} />
          ))}
        </ul>
      )}
    </div>
  );
}

function AreaCard({ area }: { area: Area }) {
  const { project } = useProject();
  const active = area.users.filter((u) => u.active);
  return (
    <li className="card area-card">
      <div className="area-card-head">
        <h2>
          <Link to={`/p/${project.key}/gebiete/${area.id}`}>{area.name}</Link>
        </h2>
        <div className="badges">
          {area.status !== 'ACTIVE' && <span className="badge badge-muted">{areaStatusLabels[area.status]}</span>}
          {!area.project_id && <span className="badge badge-warn">Projektübergreifend</span>}
          {area.kind === 'RULE' && <span className="badge">Regelgebiet</span>}
        </div>
      </div>
      <p className="muted small">Projekt: {area.projects.map((p) => p.name).join(', ') || project.name}</p>
      {area.kind === 'RULE' ? <p>{ruleText(area)}</p> : area.description && <p>{area.description}</p>}
      {area.kind !== 'RULE' && <h3>Enthält</h3>}
      {area.kind === 'RULE' ? null : area.elements.length === 0 ? (
        <p className="muted">Noch keine Landkreise, Gemeinden oder PLZ.</p>
      ) : (
        <ul className="tags">
          {area.elements.map((e) => (
            <li key={e.id} className="tag">
              {e.type === 'POSTAL_CODE' ? `PLZ ${e.code}` : `${e.type === 'COUNTY' ? e.detail : elementTypeLabels[e.type]} ${e.name}`}
            </li>
          ))}
        </ul>
      )}
      <h3>Zuständig</h3>
      {active.length === 0 ? (
        <p className="muted">Niemand zugeordnet.</p>
      ) : (
        <ul className="plain-list">
          {active.map((u) => (
            <li key={u.id}>
              {u.first_name} {u.last_name} <span className="muted">· {responsibilityLabels[u.responsibility]}</span>
              {u.is_primary && <span className="badge badge-ok">Haupt</span>}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function CreateArea() {
  const { project } = useProject();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const create = useMutation({
    mutationFn: () => api.createArea({ project_id: project.id, name: name.trim(), description: description.trim() }),
    onSuccess: (area) => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.areas(project.id) });
      navigate(`/p/${project.key}/gebiete/${area.id}`);
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };
  return (
    <form className="card inline-form" onSubmit={submit} aria-label="Neues Gebiet">
      <label className="field">
        <span>Name des neuen Gebiets</span>
        <input value={name} required minLength={2} maxLength={100} placeholder="z. B. Stuttgart Süd" onChange={(e) => setName(e.target.value)} />
      </label>
      <label className="field grow">
        <span>Beschreibung</span>
        <input value={description} maxLength={1000} onChange={(e) => setDescription(e.target.value)} />
      </label>
      <button type="submit" className="btn btn-primary" disabled={create.isPending || name.trim().length < 2}>
        Anlegen
      </button>
      {create.isError && <MutationError error={create.error} />}
    </form>
  );
}
